import React, { useState } from 'react';
import AppHeader from '../components/AppHeader';
import { useQueue } from '../state/QueueContext';
import { ArrowRightIcon, ClockIcon, TrashIcon } from '../components/icons';
import './Queue.css';

// Standalone destination reached from Home — meals saved for later. "Start
// this one" hands Home the item's original input and removes it from here.

function formatAddedAt(ts) {
  const diffMins = Math.round((Date.now() - ts) / 60000);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins} min ago`;
  const diffHours = Math.round(diffMins / 60);
  if (diffHours < 24) return `${diffHours} hr ago`;
  const diffDays = Math.round(diffHours / 24);
  return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
}

export default function Queue({ onBack, onStartItem }) {
  const { items, addToQueue, removeFromQueue, moveItem } = useQueue();
  const [draft, setDraft] = useState('');

  function handleAdd() {
    if (!draft.trim()) return;
    addToQueue(draft);
    setDraft('');
  }

  return (
    <div className="screen">
      <div className="screen__inner">
        <AppHeader onBack={onBack} />
        <h1 className="screen__title">Queue</h1>

        <section className="card">
          <p className="card__label">Save a meal for later</p>
          <div className="queue-add-input">
            <textarea
              className="queue-add-input__field"
              placeholder="Describe a meal, or paste a cooking video link..."
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              rows={2}
            />
          </div>
          <button
            type="button"
            className="button button--secondary queue-add-button"
            disabled={!draft.trim()}
            onClick={handleAdd}
          >
            Add to queue
          </button>
        </section>

        {items.length === 0 ? (
          <section className="card queue-empty">
            <p className="card__label">Your queue is empty</p>
            <p className="queue-empty__body">Save meal ideas here and start them whenever you're ready.</p>
            <button type="button" className="button button--primary" onClick={onBack}>
              Back to Home
            </button>
          </section>
        ) : (
          <ul className="queue-list">
            {items.map((item, index) => (
              <li key={item.id} className="card queue-item">
                <div className="queue-item__top">
                  <div className="queue-item__body">
                    <p className="queue-item__name">{item.mealName}</p>
                    <p className="meta-row queue-item__meta">
                      <ClockIcon size={13} /> Added {formatAddedAt(item.addedAt)}
                    </p>
                  </div>

                  <div className="queue-item__reorder">
                    <button
                      type="button"
                      className="queue-item__move"
                      aria-label="Move up"
                      disabled={index === 0}
                      onClick={() => moveItem(item.id, -1)}
                    >
                      ▲
                    </button>
                    <button
                      type="button"
                      className="queue-item__move"
                      aria-label="Move down"
                      disabled={index === items.length - 1}
                      onClick={() => moveItem(item.id, 1)}
                    >
                      ▼
                    </button>
                  </div>
                </div>

                <div className="queue-item__actions">
                  <button
                    type="button"
                    className="button button--primary queue-item__start"
                    onClick={() => onStartItem(item.id)}
                  >
                    Start this one <ArrowRightIcon size={16} />
                  </button>
                  <button
                    type="button"
                    className="queue-item__remove"
                    aria-label="Remove from queue"
                    onClick={() => removeFromQueue(item.id)}
                  >
                    <TrashIcon size={16} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
